import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorState } from '../components/QueryState';
import { relativeTime, absoluteTime, bytes } from '../lib/format';
import { canonicalize, atomHash, signBundle, type AtomDrafts, type SignedAtom } from '../lib/signing';

type StatusResponse = {
  agent_on_disk: Record<string, { file: string; sha256: string } | null>;
  clients: Array<{
    client_uuid: string;
    hostname: string | null;
    pending_files: number;
    pending_actions: number;
    assembly_present: boolean;
    assembly_files: number;
    assembly_actions: number;
    assembly_expires_at: number | null;
    assembly_atom_hash: string | null;
    pushed_at: number | null;
    assembly_stale: boolean;
    agent_current: boolean;
  }>;
};

type UnsignedResponse = {
  key_id: string;
  pubkey_pem: string;
  atom_drafts: AtomDrafts;
};

type PubkeyResponse = { key_id: string; pubkey_pem: string };

type SignerDownloadInfo =
  | { available: false }
  | {
      available: true;
      version: string;
      filename: string;
      size_bytes: number;
      sha256: string;
      built_at: number;
    };

function webCryptoSupported(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof (crypto as Crypto & { subtle?: SubtleCrypto }).subtle !== 'undefined' &&
    // Best-effort feature detection. Real check happens at importKey time.
    true
  );
}

export function Signing() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['signed-bundle', 'status'],
    queryFn: () => api.get<StatusResponse>('/signed-bundle/status'),
    refetchInterval: 60_000,
  });
  const pubkey = useQuery({
    queryKey: ['signed-bundle', 'pubkey'],
    queryFn: () => api.get<PubkeyResponse>('/signed-bundle/pubkey'),
  });
  const signerDownload = useQuery({
    queryKey: ['signed-bundle', 'signer-download', 'info'],
    queryFn: () => api.get<SignerDownloadInfo>('/signed-bundle/signer-download/info'),
  });

  const [expirySeconds, setExpirySeconds] = useState<number>(0);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const push = useMutation({
    mutationFn: async (file: File) => {
      setError(null);
      setLastResult(null);
      if (!webCryptoSupported()) {
        throw new Error('Your browser does not expose WebCrypto.subtle — use the offline tarball flow instead.');
      }
      const pemText = await file.text();
      const drafts = await api.get<UnsignedResponse>('/signed-bundle/unsigned');
      const expiresAt = expirySeconds > 0 ? Math.floor(Date.now() / 1000) + expirySeconds : null;
      const bundle = await signBundle(pemText, drafts.atom_drafts, expiresAt, drafts.key_id);
      const res = await api.post<{ ok: boolean; atoms_stored: number; assemblies_updated: number }>(
        '/signed-bundle',
        bundle,
      );
      return res;
    },
    onSuccess: (res) => {
      setLastResult(`Pushed: ${res.atoms_stored} atoms, ${res.assemblies_updated} assemblies.`);
      qc.invalidateQueries({ queryKey: ['signed-bundle'] });
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  async function downloadOfflineTarball() {
    const drafts = await api.get<UnsignedResponse>('/signed-bundle/unsigned');
    const blob = new Blob([JSON.stringify(drafts, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unsigned-bundle.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (status.isError) return <ErrorState error={status.error} onRetry={() => status.refetch()} />;
  if (status.isLoading || !status.data) return <div className="text-muted">Loading…</div>;

  const supported = webCryptoSupported();

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Offline signing</h1>
      <p className="text-sm text-muted max-w-2xl">
        v2 puts the manifest signing key OFF the server. Sign atoms with an Ed25519 key held only by you;
        clients verify against the pubkey pinned at install time. A full server compromise can DoS but
        cannot forge.
      </p>

      <section className="card space-y-2">
        <h2 className="font-semibold">Pinned public key</h2>
        {pubkey.data ? (
          <>
            <div className="text-xs text-muted">key_id: <code>{pubkey.data.key_id}</code></div>
            <pre className="text-xs whitespace-pre-wrap break-all bg-muted/10 p-2 rounded">{pubkey.data.pubkey_pem}</pre>
            <div className="flex gap-2">
              <button
                className="btn"
                onClick={() => navigator.clipboard.writeText(pubkey.data!.pubkey_pem)}
              >
                Copy
              </button>
              <button
                className="btn"
                onClick={() => {
                  const blob = new Blob([pubkey.data!.pubkey_pem], { type: 'application/x-pem-file' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'manifest-pubkey.pem';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download
              </button>
              <button className="btn" disabled title="Rotation requires re-enrolling all hosts — out of scope for v2">
                Rotate
              </button>
            </div>
          </>
        ) : <div className="text-muted">Loading…</div>}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold">Windows signer (desktop app)</h2>
        <p className="text-sm text-muted max-w-2xl">
          A native Windows GUI that owns the private key on a trusted admin machine and drives the
          whole v2 lifecycle — import or generate the key, sign in, see fleet status, fetch
          unsigned drafts, sign locally with a passphrase-encrypted PEM, and push. Use this in
          preference to the in-browser flow below when you don't want the key material exposed to a
          browser process.
        </p>
        {signerDownload.isLoading && <div className="text-muted text-sm">Checking…</div>}
        {signerDownload.data && !signerDownload.data.available && (
          <div className="text-warning text-sm">
            Installer not built yet. On the build host run{' '}
            <code>cd tools/host-customize-signer &amp;&amp; npm run dist</code>; the file will
            appear at <code>tools/host-customize-signer/dist/HostCustomizeSigner-Setup-*.exe</code>.
          </div>
        )}
        {signerDownload.data && signerDownload.data.available && (
          <div className="space-y-2">
            <div className="text-xs text-muted">
              <span className="mr-3">version <code>{signerDownload.data.version}</code></span>
              <span className="mr-3">{bytes(signerDownload.data.size_bytes)}</span>
              <span title={absoluteTime(signerDownload.data.built_at)}>
                built {relativeTime(signerDownload.data.built_at)}
              </span>
            </div>
            <div className="text-xs text-muted">
              sha256 <code className="break-all">{signerDownload.data.sha256}</code>
            </div>
            <div className="flex gap-2 items-center">
              <a
                className="btn"
                href="/admin/api/signed-bundle/signer-download"
                download={signerDownload.data.filename}
              >
                Download {signerDownload.data.filename}
              </a>
              <button
                className="btn"
                onClick={() => {
                  const info = signerDownload.data;
                  if (info && info.available) navigator.clipboard.writeText(info.sha256);
                }}
              >
                Copy sha256
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold">Push signed bundle</h2>
        <div className="text-sm text-muted">
          Pick your Ed25519 PEM private key. The key is read into browser memory only — it never goes to the server.
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted">
            Expiry:
            <select
              className="ml-2 bg-muted/10 px-2 py-1 rounded text-fg"
              value={expirySeconds}
              onChange={(e) => setExpirySeconds(Number(e.target.value))}
            >
              <option value={0}>none</option>
              <option value={86400}>24 hours</option>
              <option value={604800}>7 days</option>
              <option value={2592000}>30 days</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="file"
            accept=".pem,application/x-pem-file"
            disabled={!supported || push.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) push.mutate(f);
              e.target.value = '';
            }}
          />
          {push.isPending && <span className="text-muted text-xs">Signing + pushing…</span>}
        </div>
        {!supported && (
          <div className="text-warning text-xs">
            This browser doesn&apos;t expose WebCrypto.subtle. Use the offline tarball flow instead.
          </div>
        )}
        <div>
          <button className="btn" onClick={downloadOfflineTarball}>Download unsigned bundle (for offline signing)</button>
          <span className="ml-2 text-xs text-muted">
            Run <code>npm run sign-bundle -- --key=… --out=DIR</code> against this on a trusted machine.
          </span>
        </div>
        {lastResult && <div className="text-success text-sm">{lastResult}</div>}
        {error && <pre className="text-danger text-xs whitespace-pre-wrap">{error}</pre>}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold">Bundle status per client</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Hostname / UUID</th>
              <th>Assembly</th>
              <th>Pushed</th>
              <th>Expires</th>
              <th>Files / Actions</th>
              <th>Agent</th>
            </tr>
          </thead>
          <tbody>
            {status.data.clients.length === 0 && (
              <tr><td colSpan={6} className="text-muted">No enrolled clients yet.</td></tr>
            )}
            {status.data.clients.map((c) => {
              const expiry = c.assembly_expires_at;
              const expired = expiry != null && expiry < Date.now() / 1000;
              return (
                <tr key={c.client_uuid}>
                  <td>
                    <div className="font-mono text-xs">{c.hostname || c.client_uuid.slice(0, 8)}</div>
                    <div className="text-xs text-muted">{c.client_uuid}</div>
                  </td>
                  <td>
                    {!c.assembly_present && <span className="text-danger">✗ not signed</span>}
                    {c.assembly_present && expired && <span className="text-danger">✗ expired</span>}
                    {c.assembly_present && !expired && c.assembly_stale && (
                      <span
                        className="text-warning"
                        title="The signed assembly no longer matches the current manifest/files (e.g. a renamed destination). Re-sign and push to deploy the change."
                      >
                        ⚠ stale — re-sign
                      </span>
                    )}
                    {c.assembly_present && !expired && !c.assembly_stale && (
                      <span className="text-success">✓ signed</span>
                    )}
                  </td>
                  <td title={c.pushed_at ? absoluteTime(c.pushed_at) : undefined}>
                    {c.pushed_at ? relativeTime(c.pushed_at) : '—'}
                  </td>
                  <td>{expiry ? (expired ? 'expired' : relativeTime(expiry)) : 'never'}</td>
                  <td className={c.assembly_present && !expired && c.assembly_stale ? 'text-warning' : undefined}>
                    {c.assembly_files} / {c.assembly_actions} (want {c.pending_files} / {c.pending_actions})
                  </td>
                  <td>{c.agent_current ? '✓ current' : '⚠ stale'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
